import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { IncludeDeletedRequestSchema } from '../../shared/include-deleted.schema';
import { UserSchema, UserStatusSchema, UserClassificationSchema } from '../../entities/user/user.schema';
/**
 * List Org Members Request Schema
 * GET /api/v2/org-members?status=active&status=pending&departmentId=123&classification=internal
 *
 * Query parameters:
 * - status: Optional filter by user status. Can be single value or array.
 *           When omitted, returns all users regardless of status.
 * - departmentId: Optional filter by department ID. Returns only users in that department.
 * - classification: Optional filter by user classification. Can be single value or array.
 *                   When omitted, returns all users regardless of classification.
 * - withHomeOrgStatus: 'true' opts into homeOrgDeleted on each item. Off by
 *   default — this is one of the hottest tenant endpoints and the flag costs
 *   two extra super-admin-DB queries.
 * - withSignInReset: 'true' opts into signInResetAvailable on each item. Off
 *   by default — it costs the identity query (shared with withHomeOrgStatus)
 *   plus, for a system-admin viewer, a staff-membership query.
 *
 * Examples:
 * - /api/v2/org-members (all users)
 * - /api/v2/org-members?status=active (only active users)
 * - /api/v2/org-members?status=active&status=pending (active or pending users)
 * - /api/v2/org-members?departmentId=123 (only users in department 123)
 * - /api/v2/org-members?departmentId=123&status=active (active users in department 123)
 * - /api/v2/org-members?classification=internal (only internal members)
 */ export const ListOrgMembersRequestSchema = IncludeDeletedRequestSchema.extend({
    status: z.union([
        UserStatusSchema,
        z.array(UserStatusSchema)
    ]).optional(),
    departmentId: z.string().optional(),
    classification: z.union([
        UserClassificationSchema,
        z.array(UserClassificationSchema)
    ]).optional(),
    withHomeOrgStatus: z.enum([
        'true',
        'false'
    ]).optional(),
    withSignInReset: z.enum([
        'true',
        'false'
    ]).optional()
});
/**
 * Endpoint-scoped list item — the shared UserSchema plus the opt-in flags.
 * homeOrgDeleted (withHomeOrgStatus): the member's identity is pinned to a
 * soft-deleted tenant's org, locking the account out until a superadmin
 * assigns a new home organization. signInResetAvailable (withSignInReset):
 * the caller may reset this member's sign-in (see the reset-sign-in
 * endpoint) — computed per caller.
 */ export const OrgMemberListItemSchema = UserSchema.extend({
    homeOrgDeleted: z.boolean().optional(),
    signInResetAvailable: z.boolean().optional()
});
/**
 * List Org Members Response Schema
 * Returned on successful retrieval
 * Contains all organization members for the current tenant
 */ export const ListOrgMembersResponseSchema = z.array(OrgMemberListItemSchema);
/**
 * List Org Members Error Schema
 * Returned when retrieval fails
 */ export const ListOrgMembersErrorSchema = BaseErrorSchema;

//# sourceMappingURL=list-org-members.schema.js.map